import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { ChevronDown, Undo, Redo, Bold, Italic, Underline, Strikethrough, Link, List, AlignLeft, AlertCircle, X } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getPlantcodes, getLocation, getStatus, getKeys, getOiReferences } from '../../services/locationService';
import { saveIsolations, updateIsolation } from '../../services/hvWorkPermitService';

const isRequirementEnabled = (value: unknown): boolean =>
  value === true || value === 1 || (typeof value === 'string' && ['yes', 'true', '1'].includes(value.toLowerCase()));

const getOiReferenceIds = (item: any): string[] =>
  (Array.isArray(item.oi_reference_ids) ? item.oi_reference_ids : item.oi_reference_id != null ? [item.oi_reference_id] : []).map(String);

const getOiReferenceLabel = (reference: any): string =>
  String(
    reference?.reference_number ||
      reference?.title ||
      reference?.name ||
      reference?.reference ||
      reference?.oi_reference ||
      reference?.id ||
      'N/A',
  );

const validationSchema = Yup.object({
  plantCode: Yup.number().required('Plant Code is required'),
  location: Yup.string().required('Location is required'),
  description: Yup.string().max(500, 'Description must be 500 characters or less'),
  status: Yup.string().required('Status is required'),
  key: Yup.string().required('Key is required'),
  ops: Yup.string().required('OPS is required'),
});

interface PlantCode {
  id: number;
  title: string;
}

interface Location {
  id: number;
  title: string;
}

interface Status {
  id: number;
  title: string;
}

interface Key {
  id: number;
  title: string;
}

interface IsolationsAndInstructionProps {
  permitId: number | null;
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  triggerSubmit?: number;
  onSubmitSuccess?: (data: any) => void;
  existingIsolations?: any[];
  isolationsSaved?: boolean;
  onSessionIsolationsChange?: (items: any[]) => void;
  draftValues?: any;
  onDraftChange?: (values: any) => void;
}

const IsolationsAndInstruction = forwardRef<any, IsolationsAndInstructionProps>(
  (
    {
      permitId,
      onDataChange,
      onValidationChange,
      triggerSubmit = 0,
      onSubmitSuccess,
      existingIsolations = [],
      isolationsSaved = false,
      onSessionIsolationsChange,
      draftValues,
      onDraftChange,
    },
    ref,
  ) => {
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const [plantCodes, setPlantCodes] = useState<PlantCode[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [status, setStatus] = useState<Status[]>([]);
    const [keys, setKeys] = useState<Key[]>([]);
    const [oiReferences, setOiReferences] = useState<any[]>([]);
    const [isOiDropdownOpen, setIsOiDropdownOpen] = useState(false);
    const [oiSearch, setOiSearch] = useState('');
    const oiWrapperRef = useRef<HTMLDivElement>(null);
    const oiTagsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
        if (oiWrapperRef.current && !oiWrapperRef.current.contains(event.target as Node)) setIsOiDropdownOpen(false);
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);
    const [loading, setLoading] = useState(true);

    const emptyIsolation = {
      id: null as number | null,
      hv_work_permit_id: null as number | null,
      plant_code: null as PlantCode | null,
      plant_code_id: '',
      location: null as Location | null,
      location_id: '',
      description: '',
      requirements: false,
      items: [] as any[],
    };
    const [currentIsolation, setCurrentIsolation] = useState<any>(emptyIsolation);

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const nextTemporaryItemIdRef = useRef(-1);
    const formRef = useRef<HTMLFormElement>(null);
    const addButtonRef = useRef<HTMLButtonElement>(null);

    const lastSyncedIsolationRef = useRef<string>('');
    const existingItemsRef = useRef<any[]>([]);
    existingItemsRef.current = Array.isArray(existingIsolations?.[0]?.items) ? existingIsolations[0].items : [];
    const [validationError, setValidationError] = useState<string | null>(null);

    const emptyValues = {
      id: '',
      plantCode: '',
      location: '',
      description: '',
      requirements: false,
      status: '',
      key: '',
      ops: '',
      oiReferenceIds: [] as string[],
    };

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const [plantCodesData, locationsData, statusData, keysData] = await Promise.all([
            getPlantcodes(),
            getLocation(),
            getStatus(),
            getKeys(),
          ]);
          setPlantCodes(plantCodesData);
          setLocations(locationsData);
          setStatus(statusData);
          setKeys(keysData);
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, []);

    useEffect(() => {
      getOiReferences()
        .then((response) => {
          const references = Array.isArray(response) ? response : (response?.data?.data ?? response?.data ?? []);
          setOiReferences(Array.isArray(references) ? references : []);
        })
        .catch(() => setSubmitError('Failed to load OI references. Please try again.'));
    }, []);

    useEffect(() => {
      if (existingIsolations?.length) {
        const iso = existingIsolations[0];

        const signature = JSON.stringify({
          id: iso.id ?? null,
          plant_code_id: iso.plant_code_id || '',
          location_id: iso.location_id || '',
          description: iso.description || '',
          requirements: iso.requirements || false,
          items: iso.items || [],
        });

        if (signature === lastSyncedIsolationRef.current) {
          return;
        }
        lastSyncedIsolationRef.current = signature;

        setCurrentIsolation({
          id: iso.id ?? null,
          hv_work_permit_id: iso.hv_work_permit_id ?? null,
          plant_code: iso.plant_code ?? null,
          plant_code_id: iso.plant_code_id || '',
          location: iso.location ?? null,
          location_id: iso.location_id || '',
          description: iso.description || '',
          requirements: iso.requirements || false,
          items: Array.isArray(iso.items)
            ? iso.items.map((item: any) => ({
                ...item,
                status: item.status_id?.toString() || item.status?.toString() || '',
                key: item.key_id?.toString() || item.key?.toString() || '',
                oi_reference_ids: getOiReferenceIds(item).map(Number),
              }))
            : [],
        });
        if (editingItemId === null) {
          formik.setValues({
            id: '',
            plantCode: iso.plant_code_id?.toString() || '',
            location: iso.location_id?.toString() || '',
            description: iso.description || '',
            requirements: isRequirementEnabled(iso.requirements),
            status: '',
            key: '',
            ops: '',
            oiReferenceIds: [] as string[],
          });
        }
      }
    }, [existingIsolations]);

    useEffect(() => {
      if (onSessionIsolationsChange) {
        // When this step is mounted again, hydrate its local state from the
        // parent before publishing anything back. Without this guard the
        // initial empty local state can overwrite the previously added rows.
        if (existingItemsRef.current.length > 0 && currentIsolation.items.length === 0) {
          return;
        }

        lastSyncedIsolationRef.current = JSON.stringify({
          id: currentIsolation.id ?? null,
          plant_code_id: currentIsolation.plant_code_id || '',
          location_id: currentIsolation.location_id || '',
          description: currentIsolation.description || '',
          requirements: currentIsolation.requirements || false,
          items: currentIsolation.items || [],
        });

        onSessionIsolationsChange([currentIsolation]);
      }
    }, [currentIsolation]);

    useEffect(() => {
      if (triggerSubmit > 0 && onSubmitSuccess) {
        const saveAllIsolations = async () => {
          try {
            setValidationError(null);

            if (editingItemId !== null) {
              setValidationError('You have an unsaved edit in progress. Please click "Update" or "Cancel Edit" before proceeding.');
              return;
            }

            if (!permitId) {
              console.error('Permit ID is required');
              return;
            }

            if (currentIsolation.items.length === 0) {
              setValidationError('At least one valid operational instruction is required. Please add an item before proceeding.');
              return;
            }

            const items = currentIsolation.items.map((item: any) => ({
              status_id: Number(item.status_id ?? item.status),
              key_id: Number(item.key_id ?? item.key),
              ops: item.ops,
              oi_reference_ids: getOiReferenceIds(item).map(Number),
            }));

            const payload = {
              hv_work_permit_id: permitId,
              plant_code_id: Number(formik.values.plantCode),
              location_id: Number(formik.values.location),
              describe_components: formik.values.description,
              requirements: formik.values.requirements,
              items: items,
            };
            let response;
            if (isolationsSaved && currentIsolation.id) {
              response = await updateIsolation(currentIsolation.id, payload);
            } else {
              response = await saveIsolations(payload);
            }

            const savedId = response?.data?.id || response?.data?.[0]?.id || currentIsolation.id;
            const updatedIsolation = {
              ...currentIsolation,
              id: savedId,
              plant_code: plantCodes.find((code) => code.id === Number(formik.values.plantCode)) || null,
              plant_code_id: formik.values.plantCode,
              location: locations.find((location) => location.id === Number(formik.values.location)) || null,
              location_id: formik.values.location,
              description: formik.values.description,
              requirements: formik.values.requirements,
            };
            setCurrentIsolation(updatedIsolation);

            onSubmitSuccess(updatedIsolation);
          } catch (error) {
            console.error('Failed to save isolations:', error);
          }
        };

        saveAllIsolations();
      }
    }, [triggerSubmit, editingItemId]);

    const formik = useFormik({
      initialValues: emptyValues,
      enableReinitialize: false,
      validationSchema,
      onSubmit: async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitError(null);
          setSubmitSuccess(false);

          if (!permitId) {
            setSubmitError('Permit ID is required to create an isolation');
            setSubmitting(false);
            return;
          }

          const itemPayload = {
            status_id: Number(values.status),
            key_id: Number(values.key),
            ops: values.ops,
            oi_reference_ids: values.oiReferenceIds.map(Number),
          };

          let response;

          const activeEditId = editingItemId || (values.id ? Number(values.id) : null);

          if (activeEditId) {
            const updatePayload = {
              hv_work_permit_id: permitId,
              plant_code_id: Number(values.plantCode),
              location_id: Number(values.location),
              describe_components: values.description,
              requirements: values.requirements,
              ...itemPayload,
            };
            response = await updateIsolation(activeEditId, updatePayload);
          } else {
            const createPayload = {
              hv_work_permit_id: permitId,
              plant_code_id: Number(values.plantCode),
              location_id: Number(values.location),
              describe_components: values.description,
              requirements: values.requirements,
              items: [itemPayload],
            };
            response = await saveIsolations(createPayload);
          }

          const itemData = {
            id: response.data?.[0]?.id || response.data?.id || activeEditId,
            status_id: Number(values.status),
            status: values.status,
            key_id: Number(values.key),
            key: values.key,
            ops: values.ops,
            oi_reference_ids: values.oiReferenceIds.map(Number),
          };

          setCurrentIsolation((prev: any) => {
            const items = activeEditId
              ? prev.items.map((it: any) => (Number(it.id) === Number(activeEditId) ? itemData : it))
              : [...prev.items, itemData];
            return {
              ...prev,
              plant_code: plantCodes.find((code) => code.id === Number(values.plantCode)) || null,
              plant_code_id: values.plantCode,
              location: locations.find((location) => location.id === Number(values.location)) || null,
              location_id: values.location,
              description: values.description,
              requirements: values.requirements,
              items,
            };
          });

          if (activeEditId) {
            setEditingItemId(null);
          }

          if (onDataChange) {
            onDataChange(values);
          }

          setSubmitSuccess(true);
          resetForm();

          setTimeout(() => {
            setSubmitSuccess(false);
          }, 3000);
        } catch (error: any) {
          console.error('Failed to save isolation:', error);
          setSubmitError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to save isolation. Please try again.',
          );
        } finally {
          setSubmitting(false);
        }
      },
    });

    const hasHydratedDraft = useRef(false);
    useEffect(() => {
      if (hasHydratedDraft.current) return;

      const hasDraft =
        draftValues &&
        (draftValues.id ||
          draftValues.plantCode ||
          draftValues.location ||
          draftValues.description ||
          draftValues.requirements ||
          draftValues.status ||
          draftValues.key ||
          draftValues.ops ||
          draftValues.oiReferenceIds?.length > 0);

      if (!hasDraft) {
        hasHydratedDraft.current = true;
        return;
      }

      const draftId = draftValues.id ? Number(draftValues.id) : null;
      const belongsToCurrent = draftId !== null && currentIsolation.items.some((it: any) => Number(it.id) === draftId);

      // New, not-yet-added rows have no id and must still survive step changes.
      // Existing rows wait until their parent item has been restored so edit
      // mode remains consistent.
      if (draftId === null || belongsToCurrent) {
        formik.setValues({
          ...emptyValues,
          ...draftValues,
          oiReferenceIds: draftValues.oiReferenceIds ?? (draftValues.oiReference ? [String(draftValues.oiReference)] : []),
        });
        if (draftId !== null) setEditingItemId(draftId);
        hasHydratedDraft.current = true;
      }
    }, [currentIsolation.items, draftValues]);

    useEffect(() => {
      onDraftChange?.(formik.values);
    }, [formik.values]);

    const handleAddIsolation = async () => {
      const values = formik.values;

      const errors = await formik.validateForm();
      await formik.setTouched({
        plantCode: true,
        location: true,
        status: true,
        key: true,
        ops: true,
      });

      if (Object.keys(errors).length > 0) {
        return;
      }

      const activeEditId = editingItemId ?? (values.id ? Number(values.id) : null);
      const itemId = activeEditId ?? nextTemporaryItemIdRef.current--;

      const plantCodeDetails = plantCodes.find((pc) => pc.id === Number(values.plantCode));
      const locationDetails = locations.find((loc) => loc.id === Number(values.location));

      const itemData = {
        id: itemId,
        status_id: Number(values.status),
        status: values.status,
        key_id: Number(values.key),
        key: values.key,
        ops: values.ops,
        oi_reference_ids: values.oiReferenceIds.map(Number),
      };

      setCurrentIsolation((prev: any) => {
        const items =
          activeEditId !== null
            ? prev.items.map((it: any) => (Number(it.id) === Number(activeEditId) ? itemData : it))
            : [...prev.items, itemData];
        return {
          ...prev,
          plant_code: plantCodeDetails,
          plant_code_id: values.plantCode,
          location: locationDetails,
          location_id: values.location,
          description: values.description,
          requirements: values.requirements,
          items,
        };
      });

      if (activeEditId !== null) {
        setEditingItemId(null);
      }

      setValidationError(null);

      // formik.resetForm();
      const clearedValues = {
        ...emptyValues,
        plantCode: values.plantCode,
        location: values.location,
        description: values.description,
        requirements: values.requirements,
      };
      formik.setValues(clearedValues);
      onDraftChange?.(clearedValues);

      formik.setTouched(
        {
          ...formik.touched,
          plantCode: false,
          location: false,
          status: false,
          key: false,
          ops: false,
        },
        false,
      );
    };

    const handleEditItem = (item: any) => {
      setEditingItemId(Number(item.id));

      formik.setValues({
        id: item.id?.toString() || '',
        plantCode: formik.values.plantCode || currentIsolation.plant_code_id?.toString() || '',
        location: formik.values.location || currentIsolation.location_id?.toString() || '',
        description: formik.values.description || currentIsolation.description || '',
        requirements: formik.values.requirements,
        status: item.status_id?.toString() || item.status?.toString() || '',
        key: item.key_id?.toString() || item.key?.toString() || '',
        ops: item.ops || '',
        oiReferenceIds: getOiReferenceIds(item),
      });

      formik.setTouched(
        {
          plantCode: false,
          location: false,
          status: false,
          key: false,
          ops: false,
        },
        false,
      );

      setValidationError(null);
      setSubmitError(null);
      setExpandedItem(null);
    };

    const handleCancelEdit = () => {
      setEditingItemId(null);
      formik.setValues({
        ...emptyValues,
        plantCode: formik.values.plantCode,
        location: formik.values.location,
        description: formik.values.description,
        requirements: formik.values.requirements,
      });
    };

    useImperativeHandle(ref, () => ({
      validateForm: async () => {
        setValidationError(null);

        const hasInstructionDraft = Boolean(
          formik.values.id || formik.values.status || formik.values.key || formik.values.ops || formik.values.oiReferenceIds.length,
        );

        const mustValidateInstruction = currentIsolation.items.length === 0 || editingItemId !== null || hasInstructionDraft;
        const errors = await formik.validateForm();
        await formik.setTouched(
          {
            plantCode: true,
            location: true,
            description: true,
            status: mustValidateInstruction,
            key: mustValidateInstruction,
            ops: mustValidateInstruction,
          },
          false,
        );

        const fieldsToValidate = ['plantCode', 'location', 'description', ...(mustValidateInstruction ? ['status', 'key', 'ops'] : [])];
        const firstInvalidField = fieldsToValidate.find((field) => errors[field]);

        if (firstInvalidField) {
          requestAnimationFrame(() => {
            const field = formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalidField}"]`);
            field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field?.focus({ preventScroll: true });
          });
          return false;
        }

        if (editingItemId !== null) {
          setValidationError('You have an unsaved edit in progress. Please click "Update" or "Cancel Edit" before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        if (currentIsolation.items.length === 0) {
          setValidationError('At least one valid operational instruction is required. Please add an item before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        if (hasInstructionDraft) {
          setValidationError('Please add the completed instruction before proceeding.');
          requestAnimationFrame(() => addButtonRef.current?.focus());
          return false;
        }

        return true;
      },
      getValues: () => ({
        ...currentIsolation,
        plant_code: plantCodes.find((code) => code.id === Number(formik.values.plantCode)) || currentIsolation.plant_code,
        plant_code_id: formik.values.plantCode,
        location: locations.find((location) => location.id === Number(formik.values.location)) || currentIsolation.location,
        location_id: formik.values.location,
        description: formik.values.description,
        requirements: formik.values.requirements,
        items: currentIsolation.items,
      }),
    }));

    const getStatusColor = (status: string) => {
      const statusLower = status?.toLowerCase() || '';
      if (statusLower === 'in progress') return { dot: 'bg-orange-500', text: 'text-orange-500' };
      if (statusLower === 'completed') return { dot: 'bg-green-500', text: 'text-green-500' };
      if (statusLower === 'pending') return { dot: 'bg-gray-500', text: 'text-gray-500' };
      if (statusLower === 'on hold') return { dot: 'bg-yellow-500', text: 'text-yellow-500' };
      return { dot: 'bg-gray-500', text: 'text-gray-500' };
    };

    const isAddDisabled =
      formik.isSubmitting ||
      !formik.values.plantCode ||
      !formik.values.location ||
      !formik.values.status ||
      !formik.values.key ||
      !formik.values.ops;

    return (
      <div className="max-w-7xl mx-auto bg-white">
        <form
          ref={formRef}
          onSubmit={(event) => {
            event.preventDefault();
            void handleAddIsolation();
          }}
          className="space-y-6"
        >
          {submitSuccess && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">✓ Isolation {editingItemId ? 'updated' : 'created'} successfully!</p>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">✕ {submitError}</p>
            </div>
          )}

          {/* Edit mode banner */}
          {editingItemId && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <p className="text-sm text-blue-800 font-medium">✏️ Editing item</p>
              <button type="button" onClick={handleCancelEdit} className="text-sm text-blue-600 underline hover:text-blue-800">
                Cancel Edit
              </button>
            </div>
          )}

          <div>
            <h3 className="text-base font-semibold text-gray-900">Isolation Details</h3>
            <p className="text-sm text-gray-500 mt-1">These details apply to every operational instruction below.</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Plant Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="plantCode"
                  value={formik.values.plantCode}
                  // onChange={formik.handleChange}
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={loading || formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.plantCode && formik.errors.plantCode ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.plantCode ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Plant Code</option>
                  {plantCodes.map((plantCode: PlantCode) => (
                    <option key={plantCode.id} value={plantCode.id}>
                      {plantCode.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.plantCode && formik.errors.plantCode && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.plantCode)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Select Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="location"
                  value={formik.values.location}
                  // onChange={formik.handleChange}
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={loading || formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.location && formik.errors.location ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.location ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Location</option>
                  {locations.map((location: Location) => (
                    <option key={location.id} value={location.id}>
                      {location.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.location && formik.errors.location && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.location)}</div>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
              Describe Components/Apparatus/Instructions
              <span className="ml-2 text-gray-400 cursor-help">ⓘ</span>
            </label>

            <div className="border border-gray-300 rounded-t bg-gray-100 px-3 py-2 flex items-center space-x-2">
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Undo className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Redo className="w-4 h-4 text-gray-600" />
              </button>

              <div className="relative">
                <button
                  type="button"
                  className="px-3 py-1.5 hover:bg-gray-200 rounded flex items-center text-sm text-gray-700"
                  disabled={formik.isSubmitting}
                >
                  Normal
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
              </div>

              <div className="relative">
                <button type="button" className="p-1.5 hover:bg-gray-200 rounded flex items-center" disabled={formik.isSubmitting}>
                  <AlignLeft className="w-4 h-4 text-gray-600" />
                  <ChevronDown className="w-3 h-3 ml-1 text-gray-600" />
                </button>
              </div>

              <div className="relative">
                <button
                  type="button"
                  className="p-1.5 hover:bg-gray-200 rounded flex items-center bg-black text-white"
                  disabled={formik.isSubmitting}
                >
                  <div className="w-4 h-4 bg-black rounded-sm"></div>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </div>

              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Bold className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Italic className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Underline className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Strikethrough className="w-4 h-4 text-gray-600" />
              </button>
              <button type="button" className="p-1.5 hover:bg-gray-200 rounded" disabled={formik.isSubmitting}>
                <Link className="w-4 h-4 text-gray-600" />
              </button>

              <div className="relative">
                <button type="button" className="p-1.5 hover:bg-gray-200 rounded flex items-center" disabled={formik.isSubmitting}>
                  <List className="w-4 h-4 text-gray-600" />
                  <ChevronDown className="w-3 h-3 ml-1 text-gray-600" />
                </button>
              </div>
            </div>

            <textarea
              name="description"
              value={formik.values.description}
              maxLength={500}
              onChange={(e) => {
                if (e.target.value.length > 500) return;
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              placeholder="Add description here"
              rows={6}
              className="w-full px-4 py-3 border border-t-0 border-gray-300 rounded-b focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
              disabled={formik.isSubmitting}
            />
            <div className="flex justify-between items-center mt-1">
              {formik.touched.description && formik.errors.description ? (
                <div className="text-red-500 text-sm">{String(formik.errors.description)}</div>
              ) : (
                <div />
              )}
              <div className={`text-xs ml-auto ${500 - (formik.values.description?.length || 0) <= 50 ? 'text-red-500' : 'text-gray-400'}`}>
                {formik.values.description?.length || 0}/500
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="requirements"
              id="requirements"
              checked={formik.values.requirements}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="requirements" className="ml-3 text-sm font-medium text-gray-900">
              Requirements
            </label>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-base font-semibold text-gray-900">Operational Instructions</h3>
            <p className="text-sm text-gray-500 mt-1">Add one or more Status, Key, OPS, and OI Reference entries.</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Select Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.status && formik.errors.status ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.status ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Status</option>
                  {status && status.length > 0 ? (
                    status.map((stat: any) => (
                      <option key={stat.id} value={stat.id}>
                        {stat.title || stat.name || stat.status}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading statuses...</option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.status && formik.errors.status && (
                <div className="text-red-500 text-sm mt-1">{String(formik.errors.status)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Key <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="key"
                  value={formik.values.key}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  className={`w-full px-4 py-2.5 border ${
                    formik.touched.key && formik.errors.key ? 'border-red-500' : 'border-gray-300'
                  } rounded appearance-none bg-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 ${
                    !formik.values.key ? 'text-gray-400' : 'text-gray-900'
                  }`}
                >
                  <option value="">Select Key</option>
                  {keys && keys.length > 0 ? (
                    keys.map((key: any) => (
                      <option key={key.id} value={key.id}>
                        {key.title || key.name || key.key}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading keys...</option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {formik.touched.key && formik.errors.key && <div className="text-red-500 text-sm mt-1">{String(formik.errors.key)}</div>}
            </div>

            <div>
              <label className="flex items-center text-sm font-medium text-gray-900 mb-2">
                Enter OPS <span className="text-red-500">*</span>
                <span className="ml-2 text-gray-400 cursor-help">ⓘ</span>
              </label>
              <input
                type="text"
                name="ops"
                value={formik.values.ops}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter OPS"
                disabled={formik.isSubmitting}
                className={`w-full px-4 py-2.5 border ${
                  formik.touched.ops && formik.errors.ops ? 'border-red-500' : 'border-gray-300'
                } rounded focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100`}
              />
              {formik.touched.ops && formik.errors.ops && <div className="text-red-500 text-sm mt-1">{String(formik.errors.ops)}</div>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">OI Reference</label>
              <div
                className="relative"
                ref={oiWrapperRef}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setIsOiDropdownOpen(false);
                }}
              >
                <div
                  role="button"
                  tabIndex={formik.isSubmitting ? -1 : 0}
                  aria-label="Select OI references"
                  aria-expanded={isOiDropdownOpen}
                  onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      if (!formik.isSubmitting) setIsOiDropdownOpen((prev) => !prev);
                    }
                  }}
                  onClick={() => {
                    if (!formik.isSubmitting) setIsOiDropdownOpen((prev) => !prev);
                  }}
                  className={`w-full min-h-[46px] px-3 py-2 border ${
                    formik.errors.oiReferenceIds && formik.touched.oiReferenceIds ? 'border-red-500' : 'border-gray-300'
                  } rounded-md bg-gray-100 flex items-center`}
                >
                  {/* Selected OI References */}
                  <div ref={oiTagsRef} className="flex-1 flex gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide">
                    {formik.values.oiReferenceIds?.length ? (
                      formik.values.oiReferenceIds
                        .map((id) => oiReferences.find((item) => String(item.id) === id) || { id })
                        .map((item) => (
                          <span
                            key={item.id}
                            className="flex-shrink-0 flex items-center gap-1.5 bg-white border border-gray-200 rounded-md px-2.5 py-1 text-sm text-gray-700"
                          >
                            {getOiReferenceLabel(item)}

                            <button
                              type="button"
                              disabled={formik.isSubmitting}
                              aria-label="Remove OI reference"
                              onClick={(e) => {
                                e.stopPropagation();

                                const next = formik.values.oiReferenceIds.filter((v: string) => v !== item.id.toString());

                                formik.setFieldValue('oiReferenceIds', next);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        ))
                    ) : (
                      <span className="text-gray-400 text-sm flex-shrink-0">Select OI Reference</span>
                    )}
                  </div>

                  {/* Arrow */}
                  <ChevronDown className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
                </div>

                {isOiDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                    {/* Search box */}
                    <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                      <input
                        type="text"
                        value={oiSearch}
                        onChange={(e) => setOiSearch(e.target.value)}
                        placeholder="Search OI reference..."
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-gray-400"
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    {oiReferences.filter((item) => getOiReferenceLabel(item).toLowerCase().includes(oiSearch.toLowerCase())).length ===
                    0 ? (
                      <div className="px-4 py-3 text-sm text-gray-400">No options found</div>
                    ) : (
                      oiReferences
                        .filter((item) => getOiReferenceLabel(item).toLowerCase().includes(oiSearch.toLowerCase()))
                        .map((item) => {
                          const idStr = item.id.toString();
                          const checked = formik.values.oiReferenceIds?.includes(idStr) || false;

                          return (
                            <label key={item.id} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                disabled={formik.isSubmitting}
                                checked={checked}
                                onChange={(e) => {
                                  const currentIds = formik.values.oiReferenceIds || [];

                                  const next = e.target.checked ? [...currentIds, idStr] : currentIds.filter((v: string) => v !== idStr);

                                  formik.setFieldValue('oiReferenceIds', next);

                                  setTimeout(() => {
                                    if (oiTagsRef.current) {
                                      oiTagsRef.current.scrollTo({
                                        left: oiTagsRef.current.scrollWidth,
                                        behavior: 'smooth',
                                      });
                                    }
                                  }, 0);
                                }}
                                className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                              />

                              <span className="text-gray-900">{getOiReferenceLabel(item)}</span>
                            </label>
                          );
                        })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Validation Error Message - At least one item required */}
          {validationError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 font-medium">{validationError}</p>
            </div>
          )}

          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddIsolation}
              disabled={isAddDisabled}
              className={`px-6 py-2.5 border-2 rounded font-medium ${
                isAddDisabled
                  ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                  : 'bg-white text-black border-black hover:bg-green-50'
              }`}
            >
              {editingItemId ? 'Update' : 'Add'}
            </button>
          </div>

          <div className="space-y-3 mt-4">
            {currentIsolation.items.map((item: any, index: number) => {
              const statusLabel =
                status.find((s: any) => s.id === Number(item.status_id ?? item.status))?.title || item.status || 'Pending';

              const keyLabel = keys.find((k: any) => k.id === Number(item.key_id ?? item.key))?.title || item.key || 'N/A';

              const oiReferenceLabel =
                getOiReferenceIds(item)
                  .map((id) => {
                    const reference = oiReferences.find((entry) => String(entry.id) === id);
                    return reference ? getOiReferenceLabel(reference) : id;
                  })
                  .join(', ') || 'N/A';
              const colors = getStatusColor(statusLabel);
              const itemKey = `isolation-item-${item.id ?? index}`;

              return (
                <div key={itemKey} className="border border-gray-300 rounded-md">
                  {/* Header */}
                  <div className="px-4 py-3 flex items-center justify-between">
                    {/* Left side - expand */}
                    <button
                      type="button"
                      onClick={() => setExpandedItem(expandedItem === itemKey ? null : itemKey)}
                      className="flex-1 flex items-center text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {index + 1}. {item.ops || 'Operational Instruction'}
                        </span>

                        <span className="flex items-center space-x-1.5">
                          <span className={`w-2 h-2 rounded-full ${colors.dot}`} />

                          <span className={`text-sm ${colors.text} font-normal`}>{statusLabel}</span>
                        </span>
                      </div>

                      <ChevronDown
                        className={`w-5 h-5 ml-auto mr-3 text-gray-600 transition-transform ${
                          expandedItem === itemKey ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* EDIT BUTTON OUTSIDE */}
                    <button
                      type="button"
                      onClick={() => handleEditItem(item)}
                      disabled={formik.isSubmitting || (editingItemId !== null && Number(editingItemId) !== Number(item.id))}
                      className={`px-4 py-1.5 text-sm border rounded transition-colors ${
                        editingItemId !== null && Number(editingItemId) !== Number(item.id)
                          ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {editingItemId === Number(item.id) ? 'Editing' : 'Edit'}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {expandedItem === itemKey && (
                    <div className="px-4 py-3 border-t border-gray-300 bg-gray-50">
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600 break-words">
                          <span className="font-medium">OPS:</span> {item.ops || 'N/A'}
                        </p>

                        <p className="text-gray-600">
                          <span className="font-medium">OI Reference:</span> {oiReferenceLabel}
                        </p>

                        <p className="text-gray-600">
                          <span className="font-medium">Key:</span> {keyLabel}
                        </p>

                        <p className="text-gray-600">
                          <span className="font-medium">Status:</span> {statusLabel}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </form>
      </div>
    );
  },
);

IsolationsAndInstruction.displayName = 'IsolationsAndInstruction';

export default IsolationsAndInstruction;
